import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Upload, FileDown, Check, X, AlertTriangle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Device Inventory Excel Import wizard.
 * Props:
 *   - open: bool
 *   - onClose: () => void
 *   - onImportComplete: (summary) => void  (called after successful commit)
 */
const DeviceInventoryImportDialog = ({ open, onClose, onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { summary, rows_valid, rows_invalid }
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [downloadingTpl, setDownloadingTpl] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setLoading(false);
    setCommitting(false);
  };

  const closeAndReset = () => {
    reset();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTpl(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/v2/device-inventory/import-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_import_perangkat.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template berhasil diunduh');
    } catch (err) {
      toast.error('Gagal download template: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDownloadingTpl(false);
    }
  };

  const handleUploadPreview = async (f) => {
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) {
      toast.error('File harus .xlsx atau .xls');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 5 MB');
      return;
    }
    setFile(f);
    setLoading(true);
    setPreview(null);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', f);
      fd.append('commit', 'false');
      const res = await axios.post(
        `${API_URL}/api/v2/device-inventory/import`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setPreview(res.data);
      if (res.data?.summary?.valid === 0 && res.data?.summary?.invalid > 0) {
        toast.warning(`Semua ${res.data.summary.invalid} baris invalid. Periksa detail di bawah.`);
      } else {
        toast.success(`Preview siap: ${res.data.summary.valid} valid, ${res.data.summary.invalid} invalid`);
      }
    } catch (err) {
      toast.error('Gagal parse file: ' + (err.response?.data?.detail || err.message));
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!file || !preview?.summary?.valid) return;
    setCommitting(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('commit', 'true');
      const res = await axios.post(
        `${API_URL}/api/v2/device-inventory/import`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      const s = res.data?.summary || {};
      toast.success(
        `Import selesai — ${s.committed_ok} berhasil disimpan${s.committed_fail ? ', ' + s.committed_fail + ' gagal' : ''}.`
      );
      if (onImportComplete) onImportComplete(s);
      closeAndReset();
    } catch (err) {
      toast.error('Gagal commit: ' + (err.response?.data?.detail || err.message));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeAndReset(); }}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Import Perangkat via Excel
          </DialogTitle>
          <DialogDescription>
            Isi data device massal dari file Excel. Format wajib: <b>account_number</b>, <b>device_brand</b>. Opsional: <b>device_model, device_sn, device_band</b>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: Download Template */}
          <div className="rounded-lg border border-slate-300 dark:border-slate-700 p-4 bg-transparent">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium text-sm">Belum punya template?</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Unduh template Excel + 2 contoh baris + inline hint (comment cell).
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                disabled={downloadingTpl}
                data-testid="download-template-btn"
              >
                <FileDown className="w-4 h-4 mr-1" />
                {downloadingTpl ? 'Menyiapkan...' : 'Download Template'}
              </Button>
            </div>
          </div>

          {/* Step 2: Upload */}
          <div className="rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              className="hidden"
              data-testid="import-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                handleUploadPreview(f);
                e.target.value = '';
              }}
            />
            <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
            <div className="text-sm text-slate-700 dark:text-slate-200 font-medium">
              {file ? file.name : 'Belum ada file dipilih'}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Format: XLSX / XLS · Max 5 MB
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              disabled={loading}
              onClick={() => fileInputRef.current?.click()}
              data-testid="pick-file-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Memproses...</>
              ) : (
                <>{file ? 'Ganti File' : 'Pilih File Excel'}</>
              )}
            </Button>
          </div>

          {/* Step 3: Preview */}
          {preview && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-slate-300 dark:border-slate-700 p-3 text-center bg-transparent">
                  <div className="text-xs text-slate-500 uppercase">Total</div>
                  <div className="text-2xl font-bold" data-testid="preview-total">{preview.summary.total}</div>
                </div>
                <div className="rounded-lg border border-emerald-400 p-3 text-center bg-transparent">
                  <div className="text-xs text-emerald-600 uppercase">Valid</div>
                  <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="preview-valid">
                    {preview.summary.valid}
                  </div>
                </div>
                <div className="rounded-lg border border-red-400 p-3 text-center bg-transparent">
                  <div className="text-xs text-red-600 uppercase">Invalid</div>
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400" data-testid="preview-invalid">
                    {preview.summary.invalid}
                  </div>
                </div>
              </div>

              {preview.rows_invalid && preview.rows_invalid.length > 0 && (
                <div className="rounded-lg border border-red-300 dark:border-red-800 p-3 bg-red-50/50 dark:bg-red-950/20">
                  <div className="flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-300 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Baris Invalid ({preview.rows_invalid.length})
                  </div>
                  <div className="max-h-40 overflow-y-auto text-xs space-y-1" data-testid="preview-invalid-list">
                    {preview.rows_invalid.map((r) => (
                      <div key={r.row_num} className="text-slate-700 dark:text-slate-300">
                        <span className="font-mono">Baris {r.row_num}</span>{' '}
                        <span className="text-slate-500">({r.account_number || '?'}):</span>{' '}
                        <span className="text-red-600 dark:text-red-400">
                          {(r.errors || []).join('; ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {preview.rows_valid && preview.rows_valid.length > 0 && (
                <div className="rounded-lg border border-emerald-300 dark:border-emerald-800 p-3 bg-emerald-50/50 dark:bg-emerald-950/20">
                  <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300 mb-2">
                    <Check className="w-4 h-4" />
                    Baris Valid ({preview.rows_valid.length}) — siap disimpan
                  </div>
                  <div className="max-h-40 overflow-y-auto text-xs space-y-1" data-testid="preview-valid-list">
                    {preview.rows_valid.map((r) => (
                      <div key={r.row_num} className="text-slate-700 dark:text-slate-300">
                        <span className="font-mono">Baris {r.row_num}</span>{' '}
                        <span className="text-slate-500">({r.account_number}):</span>{' '}
                        <b>{r.name}</b> — {r.device_brand}
                        {r.device_model && ` / ${r.device_model}`}
                        {r.device_sn && ` / SN ${r.device_sn}`}
                        {r.device_band && ` / ${r.device_band}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={closeAndReset} disabled={committing} data-testid="cancel-import-btn">
            <X className="w-4 h-4 mr-1" />
            Batal
          </Button>
          <Button
            onClick={handleCommit}
            disabled={!preview || !preview.summary?.valid || committing}
            data-testid="commit-import-btn"
          >
            {committing ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Menyimpan...</>
            ) : (
              <><Check className="w-4 h-4 mr-1" />Konfirmasi & Simpan ({preview?.summary?.valid ?? 0})</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeviceInventoryImportDialog;
